import {
  Autocomplete,
  debounce,
  TextField,
  TextFieldProps,
} from '@mui/material';

import { Field, FieldProps, useFormikContext } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import {
  SearchLocationIdAndNameQuery,
  useGetAllLocationsWithSearchLazyQuery,
  useGetLocationLazyQuery,
} from '../../generated/graphql';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiControl-root': {
      width: '100%',
    },
  },
}));

interface SelectSubcontractorProps {
  name: string;
  readOnly?: boolean;
  fullWidth?: boolean;
}

export function SelectSubcontractor(
  props: SelectSubcontractorProps & TextFieldProps,
) {
  const { name, readOnly = false, fullWidth = false } = props;
  const { t } = useTranslation();
  const { getFieldProps } = useFormikContext();
  const classes = useStyles();
  const [options, setOptions] = useState<
    SearchLocationIdAndNameQuery['locationSearch']
  >([]);
  const [value, setValue] = useState<
    SearchLocationIdAndNameQuery['locationSearch'][number] | null
  >(null);

  const [search, setSearch] = useState('');
  const [searchSubcontractors, { data: optionsData, loading: loadingOptions }] =
    useGetAllLocationsWithSearchLazyQuery({ fetchPolicy: 'network-only' });
  const [getSubcontractor, { data: subcontractorData, loading }] =
    useGetLocationLazyQuery({ fetchPolicy: 'network-only' });
  const initialValue = getFieldProps(name).value;

  useEffect(() => {
    if (initialValue != null && initialValue !== '') {
      getSubcontractor({ variables: { id: initialValue } });
    }
  }, [initialValue]);

  useEffect(() => {
    if (subcontractorData) {
      setOptions([subcontractorData.location]);
      setValue(subcontractorData.location);
    }
  }, [subcontractorData]);

  useEffect(() => {
    if (search.length > 2) {
      searchSubcontractors({
        variables: {
          search: search,
        },
      });
    }
  }, [search]);

  useEffect(() => {
    if (optionsData) {
      if (value) {
        setOptions([value, ...optionsData.allLocationWithSearch]);
      } else {
        setOptions(optionsData.allLocationWithSearch);
      }
    }
  }, [optionsData]);

  const setSearchHandler = (_event: any, newVal: string) => {
    setSearch(newVal);
  };

  const debouncedChangeHandler = useCallback(
    debounce(setSearchHandler, 300),
    [],
  );

  const nameSplit = name.split('.');
  const label = nameSplit[nameSplit.length - 1];

  if (readOnly) {
    return (
      <TextField
        {...props}
        variant="outlined"
        label={t(`attributes.${label}`)}
        value={value?.name}
        size="small"
        InputLabelProps={{
          shrink: true,
        }}
      />
    );
  }

  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps) => (
        <Autocomplete
          className={classes.root}
          onBlur={field.onBlur}
          value={value}
          multiple={false}
          fullWidth={fullWidth}
          autoHighlight
          onChange={(_event, val) => {
            form.setFieldValue(name, val?.id ?? null);
            if (!val || !val.id) {
              setOptions([]);
              setValue(null);
            }
          }}
          noOptionsText=""
          onInputChange={debouncedChangeHandler}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          getOptionLabel={(option) => {
            return option.name;
          }}
          options={options}
          loading={loading || loadingOptions}
          renderInput={(params) => (
            <TextField
              {...props}
              {...params}
              size="small"
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error}
              variant="outlined"
              label={t(`attributes.${label}`)}
              InputProps={{ ...params.InputProps }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />
      )}
    </Field>
  );
}
